import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
  LightbulbFilamentIcon,
  WalletIcon,
  GlobeIcon,
} from "@phosphor-icons/react";
import { useProviders } from "@/client/services/payment-provider/providers";
import {
  useProviderZones,
  useDeleteProviderZone,
} from "@/client/services/payment-provider/provider-zones";
import {
  useSetProviderStatus,
  useSetProviderMode,
} from "@/client/services/payment-provider/provider-status";
import { useDialog } from "@/client/stores/dialog";
import { useChgStatus } from "@/client/services/payment-provider/update-provider-zones";
import { useDeleteProvider } from "@/client/services/payment-provider/providers";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const PaymentProviderPage = () => {
  const [providerPage, setProviderPage] = useState(1);
  const [zonePage, setZonePage] = useState(1);

  const { providers } = useProviders(providerPage);
  const { providerZones } = useProviderZones(zonePage);

  const zone = useDialog("create-zone");
  const provider = useDialog("create-provider");

  const { open: editZone } = useDialog<any>("edit-zone");
  const { open: editProvider } = useDialog<any>("edit-provider");

  const deleteProviderZone = useDeleteProviderZone();
  const ChStatus = useChgStatus();
  const deleteProvider = useDeleteProvider();
  const setProviderStatus = useSetProviderStatus();
  const setProviderMode = useSetProviderMode();

  // --- Colonnes pour ProviderTable ---
  const providerColumns: Column<any>[] = [
    {
      key: "name",
      header: "Nom",
      render: (item) => <span className="font-medium text-admin-text-primary">{item.name}</span>,
    },
    {
      key: "mode",
      header: "Mode",
      render: (item) => (
        <Select
          value={item.mode ?? "live"}
          onValueChange={(mode) =>
            setProviderMode.mutate({ id: item.id, mode: mode as "live" | "sandbox" })
          }
        >
          <SelectTrigger className="h-8 w-28">
            <SelectValue placeholder="Mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="live">Live</SelectItem>
            <SelectItem value="sandbox">Sandbox</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      key: "isActive",
      header: "Actif",
      render: (item) => (
        <button
          type="button"
          role="switch"
          aria-checked={item.isActive}
          onClick={() => setProviderStatus.mutate({ id: item.id, isActive: !item.isActive })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${item.isActive ? "bg-green-500" : "bg-admin-border"}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.isActive ? "translate-x-6" : "translate-x-1"}`}
          />
        </button>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-2 text-admin-text-secondary">
          <Button
            variant="ghost"
            size="icon"
            className="hover:text-admin-text-primary"
            onClick={() => editProvider("update", { id: "edit-provider", item })}
          >
            <PencilSimpleIcon size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hover:text-admin-danger"
            onClick={() => deleteProvider.mutate(item.id)}
          >
            <TrashIcon size={18} />
          </Button>
        </div>
      ),
    },
  ];

  // --- Colonnes pour ProviderZoneTable ---
  const zoneColumns: Column<any>[] = [
    {
      key: "country",
      header: "Pays",
      render: (item) => <span className="text-admin-text-primary">{item.country}</span>,
    },
    {
      key: "currency",
      header: "Devise",
      render: (item) => <span className="text-admin-text-secondary">{item.currency}</span>,
    },
    {
      key: "priority",
      header: "Priorité",
      render: (item) => <span className="text-admin-text-secondary">{item.priority}</span>,
    },
    {
      key: "provider",
      header: "Fournisseur",
      render: (item) => (
        <span className="text-admin-text-secondary">{item.provider?.name || "N/A"}</span>
      ),
    },
    {
      key: "isActive",
      header: "Actif",
      render: (item) =>
        item.isActive ? <Badge variant="success">Oui</Badge> : <Badge variant="error">Non</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-2 text-admin-text-secondary">
          <Button
            variant="ghost"
            size="icon"
            className="hover:text-admin-text-primary"
            onClick={() => editZone("update", { id: "edit-zone", item })}
          >
            <PencilSimpleIcon size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={
              item.isActive
                ? "hover:text-admin-warning"
                : "text-admin-success hover:text-admin-success"
            }
            onClick={() => ChStatus.mutate({ id: item.id, data: { isActive: !item.isActive } })}
          >
            <LightbulbFilamentIcon size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hover:text-admin-danger"
            onClick={() => deleteProviderZone.mutate(item.id)}
          >
            <TrashIcon size={18} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">
            Fournisseurs de Paiement & Zones
          </h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Paiement`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              type="text"
              placeholder="Rechercher..."
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Fournisseurs"
              number={providers?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <WalletIcon size={24} weight="duotone" />
            </div>
          </CardContent>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Zones"
              number={providerZones?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <GlobeIcon size={24} weight="duotone" />
            </div>
          </CardContent>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page fournisseurs"
              number={providerPage.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <WalletIcon size={24} weight="duotone" />
            </div>
          </CardContent>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page zones"
              number={zonePage.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <GlobeIcon size={24} weight="duotone" />
            </div>
          </CardContent>
        </CardRoot>
      </div>

      {/* Section Actions & Tabs */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="mb-6 flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          <div className="flex gap-3">
            <Button
              onClick={() => provider.open("create", { id: "create-provider" })}
              className="h-10 bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
            >
              <PlusIcon size={16} className="mr-2" /> Nouveau fournisseur
            </Button>
            <Button
              onClick={() => zone.open("create", { id: "create-zone" })}
              className="h-10 bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
            >
              <PlusIcon size={16} className="mr-2" /> Nouvelle zone
            </Button>
          </div>
          <Button
            variant="outline"
            className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
          >
            <ExportIcon size={16} className="mr-2" /> Exporter
          </Button>
        </div>

        <Tabs defaultValue="providers" className="w-full">
          <TabsList className="rounded-lg border border-admin-border bg-admin-background p-1">
            <TabsTrigger
              value="providers"
              className="data-[state=active]:bg-admin-primary data-[state=active]:text-admin-primary-foreground"
            >
              Fournisseurs ({providers ? `${providerPage} sur ${providers.page}` : 0})
            </TabsTrigger>
            <TabsTrigger
              value="zones"
              className="data-[state=active]:bg-admin-primary data-[state=active]:text-admin-primary-foreground"
            >
              Zones ({providerZones ? `${zonePage} sur ${providerZones.page}` : 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="providers" className="mt-4">
            <DataTable data={providers?.data || []} columns={providerColumns} pageSize={10} />

            <div className="my-3 flex flex-row justify-end space-x-3">
              {providerPage > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage - 1)}
                  className="rounded-lg"
                >
                  <CaretCircleLeftIcon size={32} weight="regular" />
                </Button>
              )}
              {providerPage - 2 > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage - 2)}
                  className="rounded-lg"
                >
                  {providerPage - 2}
                </Button>
              )}
              {providerPage - 1 > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage - 1)}
                  className="rounded-lg"
                >
                  {providerPage - 1}
                </Button>
              )}

              <Button
                variant="secondary"
                className="bg-admin-primary text-admin-primary-foreground"
              >
                {providerPage}
              </Button>

              {providers && providerPage + 1 <= providers.page && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage + 1)}
                  className="rounded-lg"
                >
                  {providerPage + 1}
                </Button>
              )}
              {providers && providerPage + 2 <= providers.page && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage + 2)}
                  className="rounded-lg"
                >
                  {providerPage + 2}
                </Button>
              )}
              {providers && providerPage < providers.page && (
                <Button
                  variant="outline"
                  onClick={() => setProviderPage(providerPage + 1)}
                  className="rounded-lg"
                >
                  <CaretCircleRightIcon size={32} weight="regular" />
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="zones" className="mt-4">
            <DataTable data={providerZones?.data || []} columns={zoneColumns} pageSize={10} />

            <div className="my-3 flex flex-row justify-end space-x-3">
              {zonePage > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage - 1)}
                  className="rounded-lg"
                >
                  <CaretCircleLeftIcon size={32} weight="regular" />
                </Button>
              )}
              {zonePage - 2 > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage - 2)}
                  className="rounded-lg"
                >
                  {zonePage - 2}
                </Button>
              )}
              {zonePage - 1 > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage - 1)}
                  className="rounded-lg"
                >
                  {zonePage - 1}
                </Button>
              )}

              <Button
                variant="secondary"
                className="bg-admin-primary text-admin-primary-foreground"
              >
                {zonePage}
              </Button>

              {providerZones && zonePage + 1 <= providerZones.page && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage + 1)}
                  className="rounded-lg"
                >
                  {zonePage + 1}
                </Button>
              )}
              {providerZones && zonePage + 2 <= providerZones.page && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage + 2)}
                  className="rounded-lg"
                >
                  {zonePage + 2}
                </Button>
              )}
              {providerZones && zonePage < providerZones.page && (
                <Button
                  variant="outline"
                  onClick={() => setZonePage(zonePage + 1)}
                  className="rounded-lg"
                >
                  <CaretCircleRightIcon size={32} weight="regular" />
                </Button>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};
